import { useQuery, UseQueryOptions } from 'react-query'
import QUERIES from 'api/queries'
import { getCreditPacks, getPlans, getStripeDashboardLink } from './services'
import { useLocalization } from 'localization'
import startCase from 'lodash/startCase'
import { IBillingPlan, ICreditPack } from './models'
import { IUser } from 'api/users/models'

export const usePlans = (
	jurisdiction?: IUser['jurisdiction'],
	options?: Omit<
		UseQueryOptions<IBillingPlan[], unknown, IBillingPlan[], (string | undefined)[]>,
		'queryKey' | 'queryFn'
	>
) => {
	const { t, f } = useLocalization()

	return useQuery(
		[QUERIES.plans, jurisdiction],
		async (): Promise<IBillingPlan[]> => {
			const plans = await getPlans()
			return (
				plans
					// sort by amount 1st
					.sort((prev, next) => {
						if (!prev.amount || !next.amount) return 0
						return prev.amount > next.amount ? 1 : -1
					})
					// move free and custom 2nd
					.sort((prev, next) => {
						if (next.type === 'FREE' || prev.type === 'CUSTOM') return 1
						if (next.type === 'CUSTOM' || prev.type === 'FREE') return -1
						return 0
					})
					.map(plan => {
						return {
							...plan,
							name: t(`billings.userPlans.${plan.type}.name`),
							price:
								plan.type === 'FREE'
									? '-'
									: plan.currency && plan.amount
									? `${f(plan.amount, { currency: plan.currency.toUpperCase() })}/${t(
											`billings.userPlans.intervals.${plan.interval}`
									  )}`
									: startCase(t(`billings.userPlans.${plan.type}.interval`)),
							description: t(`billings.userPlans.description`, {
								transactions: plan.transactions,
								analysis: plan.taxAnalysis,
							}),
						}
					})
			)
		},
		options
	)
}

export const useCreditPackages = (
	jurisdiction?: IUser['jurisdiction'],
	options?: Omit<UseQueryOptions<ICreditPack[], unknown, ICreditPack[], (string | undefined)[]>, 'queryKey' | 'queryFn'>
) =>
	useQuery(
		[QUERIES.creditPacks, jurisdiction],
		async () => {
			const packs = await getCreditPacks()
			return packs.sort((prev, next) => {
				if (!prev.amount || !next.amount) return 0
				return prev.amount > next.amount ? 1 : -1
			})
		},
		options
	)

export const useStripeDashboardLink = (
	returnUrl?: string,
	options?: Omit<UseQueryOptions<string | undefined, unknown, string, string>, 'queryKey' | 'queryFn'> | undefined
) =>
	useQuery(
		QUERIES.stripeDashboardLink,
		async () => (returnUrl ? await getStripeDashboardLink({ returnUrl }) : undefined),
		options
	)
